import { AlertTriangle, Check, Copy, Inbox, ShieldAlert, WifiOff } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isApiError } from '@/lib/api/errors';
import { cn } from '@/lib/cn';
import { Button } from './button';

export const Skeleton = ({ className }: { className?: string }) => <div className={cn('skeleton h-4 w-full', className)} aria-hidden />;

export function EmptyState({ icon, title, hint, action }: { icon?: ReactNode; title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="animate-in mx-auto flex max-w-sm flex-col items-center gap-3 px-6 py-14 text-center">
      <div className="grid size-12 place-items-center rounded-full bg-surface-2 text-subtle">{icon ?? <Inbox className="size-6" aria-hidden />}</div>
      <h3 className="text-base font-semibold">{title}</h3>
      {hint && <p className="text-sm text-muted">{hint}</p>}
      {action}
    </div>
  );
}

export function CopyButton({ value, label }: { value: string; label?: string }) {
  const { t } = useTranslation();
  const [done, setDone] = useState(false);
  return (
    <Button
      variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs" aria-label={label ?? t('common.copy')}
      onClick={async () => { try { await navigator.clipboard.writeText(value); setDone(true); setTimeout(() => setDone(false), 1500); } catch { /* clipboard blocked */ } }}
    >
      {done ? <Check className="size-3.5" aria-hidden /> : <Copy className="size-3.5" aria-hidden />}{done ? t('common.copied') : t('common.copy')}
    </Button>
  );
}

/** Shows the server's checklist (`details.problems[]`) of a 422, e.g. why an invoice cannot be validated yet. */
export function ProblemList({ problems, title }: { problems: string[]; title?: string }) {
  const { t } = useTranslation();
  if (!problems.length) return null;
  return (
    <div role="alert" className="rounded-lg border border-warning-border bg-warning-soft p-4 text-sm">
      <p className="mb-2 flex items-center gap-2 font-medium text-warning"><AlertTriangle className="size-4" aria-hidden />{title ?? t('errors.problems')}</p>
      <ul className="list-disc space-y-1 ps-5 text-fg">{problems.map((p) => <li key={p}>{p}</li>)}</ul>
    </div>
  );
}

/** Error with a friendly sentence, the server's detail when useful, and the support reference. */
export function ErrorState({ error, onRetry, compact }: { error: unknown; onRetry?: () => void; compact?: boolean }) {
  const { t } = useTranslation();
  const api = isApiError(error) ? error : null;
  const forbidden = api?.code === 'FORBIDDEN';
  const offline = api?.code === 'NETWORK_ERROR';
  const Icon = forbidden ? ShieldAlert : offline ? WifiOff : AlertTriangle;
  const title = api ? t(`errors.${api.code}`) : t('errors.title');
  return (
    <div role="alert" className={cn('mx-auto flex max-w-md flex-col items-center gap-3 text-center', compact ? 'py-6' : 'py-14')}>
      <div className={cn('grid size-12 place-items-center rounded-full', forbidden ? 'bg-warning-soft text-warning' : 'bg-danger-soft text-danger')}><Icon className="size-6" aria-hidden /></div>
      <h3 className="text-base font-semibold">{title}</h3>
      {api && api.message && api.message !== title && <p className="text-sm text-muted">{api.message}</p>}
      {api && api.problems.length > 0 && <div className="w-full text-start"><ProblemList problems={api.problems} /></div>}
      <div className="flex items-center gap-2">
        {onRetry && !forbidden && <Button onClick={onRetry}>{t('common.retry')}</Button>}
        {api?.requestId && <span className="flex items-center gap-1 text-xs text-subtle">{t('common.requestId')}: <code className="font-mono">{api.requestId.slice(0, 8)}</code><CopyButton value={api.requestId} /></span>}
      </div>
    </div>
  );
}
